import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Add, LocationOn } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FlagIcon from '@mui/icons-material/Flag';
import StoreIcon from '@mui/icons-material/Store';
import {
  Autocomplete,
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  APIProvider,
  AdvancedMarker,
  InfoWindow,
  Map,
  Pin,
} from '@vis.gl/react-google-maps';

import { capitalizeFirstLetter } from '../tools/utils';
import StoreConfirmDialog from './StoreConfirmDialog';
import StoreFormDialog from './StoreFormDialog';

export default function StoreConfig(props) {
  const {
    getStoreConfigs,
    isLoadingStoreConfigs,
    storeConfigs,
    getChains,
    chainsList,
    postStoreConfig,
    newStoreResponse,
    patchStoreConfig,
    storeUpdateResponse,
    deleteStoreConfig,
    deleteStoreResponse,
    getCountries,
    countriesList,
    getMapsKey,
    gmapsApiKey,
    featurePermission,
    checkFeaturePermission,
    setSnackAlert
  } = props;

  const { t } = useTranslation();

  const [chain, setChain] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [editStore, setEditStore] = useState(null);
  const [storeAction, setStoreAction] = useState(null);
  const [showProductive, setShowProductive] = useState(true);
  const [markerColor, setMarkerColor] = useState(null);
  const [canUpdate, setCanUpdate] = useState(false);
  const [newStore, setNewStore] = useState({
    store_code: '',
    store_name: '',
    store_type: '',
    coordinates: { lat: -33.45, lng: -70.65 },
  });
  const [currentStore, setCurrentStore] = useState(null);
  const [currentZoom, setCurrentZoom] = useState(null);
  const [currentContactID, setCurrentContactID] = useState(null);

  const UPDATE_STORE_CONFIG_PERMISSION = 'update_store_config';

  useEffect(() => {
    getChains();
    getStoreConfigs();
    getCountries();
    getMapsKey();
    checkFeaturePermission(UPDATE_STORE_CONFIG_PERMISSION);
  }, []);

  useEffect(() => {
    if (UPDATE_STORE_CONFIG_PERMISSION in featurePermission) {
      setCanUpdate(!!featurePermission[UPDATE_STORE_CONFIG_PERMISSION])
    }
  }, [featurePermission]);

  useEffect(() => {
    const colors = {};
    chainsList.forEach((chain) => {
      if (chain.properties)
        colors[chain.name] = chain.properties.map_marker_colors;
    });
    setMarkerColor(colors);
  }, [chainsList]);

  useEffect(() => {
    getStoreConfigs();
  }, [newStoreResponse, storeUpdateResponse, deleteStoreResponse]);

  const handleChainChange = (event, newChain) => {
    setChain(newChain);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleCreateStore = () => {
    setStoreAction('create');
    setOpenDialog(true);
  };

  const handleOpenConfirm = (item, action) => {
    setSelectedStore(item.store_code);
    setCurrentContactID(item.contact_id);
    setConfirmAction(action);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirm = () => setOpenConfirmDialog(false);

  const handlePlaceSelect = (place) => {
    if (place && place.geometry) {
      setNewStore({
        ...newStore,
        address: place.formatted_address,
        coordinates: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      });
    }
  };

  const handleSaveStore = (storeConfig, action, storeCode = null) => {
    if (action === 'create') postStoreConfig(storeConfig);
    else if (action === 'edit') patchStoreConfig(storeCode, storeConfig);
  };

  const handleConfirmStore = () => {
    if (confirmAction === 'edit') {
    } else if (confirmAction === 'delete') {
      deleteStoreConfig(selectedStore, currentContactID);
      setOpenConfirmDialog(false);
    }
  };

  const handleEditStore = (storeConfig) => {
    setStoreAction('edit');
    setEditStore(storeConfig);
    setOpenDialog(true);
  };

  const renderSkeleton = () => (
    <ListItem alignItems="flex-start" style={{ marginBottom: '16px' }}>
      <ListItemText
        primary={<Skeleton width="60%" />}
        secondary={
          <>
            <Skeleton width="40%" />
            <Skeleton width="50%" />
            <Skeleton width="30%" />
          </>
        }
      />
      <IconButton>
        <Skeleton variant="circular" width={24} height={24} />
      </IconButton>
      <IconButton>
        <Skeleton variant="circular" width={24} height={24} />
      </IconButton>
    </ListItem>
  );

  const filteredStores = storeConfigs.filter((store) => {
    const matchesChain = chain ? store.chain_name === chain : true;
    const matchesSearch = `${store.store_name} ${store.store_code}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesProductive = showProductive
      ? store.store_type === 'production'
      : store.store_type !== 'production';
    return matchesChain && matchesSearch && matchesProductive;
  });

  const onStoreClick = (element) => {
    setCurrentStore(element.coordinates)
    setCurrentZoom(20)
  }

  const onCameraChanged = (element) => {
    setCurrentStore(null)
    setCurrentZoom(null)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper component={Grid} container p={2}>
          {/* Title and switch */}
          <Stack direction='row' justifyContent='space-between' alignItems='space-between' mb={1} sx={{ width: '100%' }}>
            <Typography variant="h5">{t('overseer_app.store_config.Store_config', 'Store Config')}</Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={showProductive}
                    onChange={() => setShowProductive(!showProductive)}
                  />
                }
                label={t('overseer_app.store_config.Productive', 'Productive')}
              />
            </FormGroup>
          </Stack>
          {/* Selector, filter and button */}
          <Grid container spacing={1} alignItems="center">
            {/* Chains selector */}
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                value={chain}
                options={chainsList.map((option) => option.name)}
                onChange={handleChainChange}
                renderInput={(params) => <TextField {...params} label={t('overseer_app.store_config.Chains', 'Chains')} />}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;
                  const backgroundColor = markerColor[option] ? markerColor[option].background : '#EA4435'
                  return (
                    <Box
                      key={key}
                      component="li"
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      {...optionProps}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                          borderRadius: '3px',
                          mr: 1,
                          mt: '2px',
                        }}
                        style={{ backgroundColor: backgroundColor }}
                      />
                      {option}
                    </Box>
                  );
                }}
              />
            </Grid>
            <Stack component={Grid} direction='row' item xs={12} md={6}>
              {/* Store filter */}
              <TextField
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={t('overseer_app.store_config.Search_store', 'Search store')}
                variant="outlined"
                fullWidth
              />
              {/* Add new store button */}
              <Tooltip title={t('overseer_app.store_config.Add_new_store', 'Add new store')}>
                <IconButton onClick={handleCreateStore}>
                  <Add color="primary" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          {isLoadingStoreConfigs
            ? Array.from({ length: 4 }).map((_, index) => (
              <React.Fragment key={index}>
                {renderSkeleton()}
              </React.Fragment>
            ))
            :
            // Stores list
            <Stack component={List} divider={<Divider flexItem />} sx={{ width: '100%', height: '70vh', overflow: 'auto' }}>
              {filteredStores.map((item) => (
                <ListItem key={item.store_code} onClick={() => onStoreClick(item)}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 'bold' }}
                      >
                        {`${item.store_code} - ${item.store_name}`}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="textSecondary">
                          <FlagIcon fontSize="small" sx={{ color: markerColor[item.chain_name] ? markerColor[item.chain_name].background : '#EA4435' }} />
                          {item.chain_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <LocationOn fontSize="small" /> {item.address}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <StoreIcon fontSize="small" />{' '}
                          {capitalizeFirstLetter(item.store_type)}
                        </Typography>
                      </>
                    }
                  />
                  <IconButton disabled={!canUpdate} onClick={() => handleEditStore(item)}>
                    <EditIcon color="primary" />
                  </IconButton>
                  <IconButton disabled={!canUpdate} onClick={() => handleOpenConfirm(item, 'delete')}>
                    <DeleteIcon color="primary" />
                  </IconButton>
                </ListItem>
              ))}
            </Stack>
          }
        </Paper>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper component={Grid} container p={2} sx={{ height: '88vh' }}>
          {gmapsApiKey && (
            <APIProvider apiKey={gmapsApiKey?.maps_key}>
              <Map
                mapId={gmapsApiKey?.maps_id}
                mapContainerStyle={{ height: '100%', width: '100%' }}
                defaultCenter={{ lat: -33.45, lng: -70.65 }}
                defaultZoom={5}
                onCameraChanged={onCameraChanged}
                center={currentStore}
                zoom={currentZoom}
              >
                {filteredStores.map(
                  (item) =>
                    item.coordinates && (
                      <AdvancedMarker
                        position={{
                          lat: item.coordinates?.lat,
                          lng: item.coordinates?.lng,
                        }}
                        title={item.store_code}
                      >
                        <Pin
                          background={
                            markerColor[item['chain_name']]?.background
                          }
                          borderColor={
                            markerColor[item['chain_name']]?.borderColor
                          }
                          glyphColor={
                            markerColor[item['chain_name']]?.glyphColor
                          }
                        />
                      </AdvancedMarker>
                    )
                )}
                {hoveredMarker && (
                  <InfoWindow
                    pixelOffset={[0, -50]}
                    position={{
                      lat: filteredStores.find(
                        (store) => store.store_code === hoveredMarker
                      )?.coordinates?.lat,
                      lng: filteredStores.find(
                        (store) => store.store_code === hoveredMarker
                      )?.coordinates?.lng,
                    }}
                    onCloseClick={() => setHoveredMarker(null)}
                  >
                    <div>
                      <Typography variant="body2">
                        <strong>
                          {
                            filteredStores.find(
                              (store) => store.store_code === hoveredMarker
                            )?.store_name
                          }
                        </strong>
                      </Typography>
                      <Typography variant="caption">
                        Code: {hoveredMarker}
                      </Typography>
                    </div>
                  </InfoWindow>
                )}
              </Map>
              <StoreFormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                apiKey={gmapsApiKey}
                onPlaceSelect={handlePlaceSelect}
                chainsList={chainsList}
                onSave={handleSaveStore}
                countries={countriesList}
                storeInfo={editStore}
                action={storeAction}
                setSnackAlert={setSnackAlert}
              />
            </APIProvider>
          )}
        </Paper>
      </Grid>
      <StoreConfirmDialog
        open={openConfirmDialog}
        action={confirmAction}
        handleClose={handleCloseConfirm}
        storeCode={selectedStore}
        handleConfirm={handleConfirmStore}
      />
    </Grid>
  );
}
